import { TypographyP2 } from '@heidi/design-system/typography';
import { Fragment, useMemo } from 'react';

import { changeTintClassName } from './change-tint';
import {
  diffLines,
  hasText,
  type ChangeRow,
  type WordDiffSegment,
} from './routine-word-diff';

type ShownRow = Exclude<ChangeRow, { kind: 'removed' }>;

export const Tint = ({ children }: { children: string }) => (
  <span data-change="" className={changeTintClassName}>
    {children}
  </span>
);

/** A word diff's After side: added words tinted, removed words left out. */
export const TintedSegments = ({ segments }: { segments: WordDiffSegment[] }) => (
  <>
    {segments.map((segment, index) => {
      if (segment.op === 'del') return null;
      if (segment.op === 'add' && hasText(segment.text)) {
        return <Tint key={index}>{segment.text}</Tint>;
      }
      return <Fragment key={index}>{segment.text}</Fragment>;
    })}
  </>
);

const RowText = ({ row }: { row: ShownRow }) => {
  if (row.kind === 'same') return <>{row.text}</>;
  if (row.kind === 'added') return hasText(row.text) ? <Tint>{row.text}</Tint> : null;
  return <TintedSegments segments={row.segments} />;
};

/** The instructions as they will be saved, with new text tinted. */
export const RoutineInstructionsChanges = ({
  before,
  after,
}: {
  before: string;
  after: string;
}) => {
  const rows = useMemo(() => diffLines(before, after), [before, after]);
  const shown = rows.filter((row): row is ShownRow => row.kind !== 'removed');
  return (
    <TypographyP2 className="whitespace-pre-line text-text-primary">
      {shown.map((row, index) => (
        <Fragment key={index}>
          {index > 0 ? '\n' : null}
          <RowText row={row} />
        </Fragment>
      ))}
    </TypographyP2>
  );
};
